import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, FolderPlus, Trash2, Plus, X, Play, Check,
  Folder, Video, Image as ImageIcon, Search, Grid, List,
  MoreVertical, ChevronRight, ChevronLeft, ChevronDown, Film, Maximize2,
  Scissors, Eye, Edit2, Pause, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import VideoTrimEditor from './VideoTrimEditor';

const API_URL = window.location.origin;

/**
 * VideoVault - A media library for storing and organizing videos/images
 * Features:
 * - Upload videos and images to a central vault
 * - Organize into folders
 * - Select media to add to storyboard projects
 * - If projectId is provided, shows only items for that project
 */
const VideoVault = ({ userId, projectId, onSelectForStoryboard }) => {
  const [vaultItems, setVaultItems] = useState([]);
  const [vaultFolders, setVaultFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [showMoveDropdown, setShowMoveDropdown] = useState(false); // Move to folder dropdown
  
  // Send to Storyboard modal state
  const [showSendToStoryboardModal, setShowSendToStoryboardModal] = useState(false);
  const [storyboardProjects, setStoryboardProjects] = useState([]);
  const [storyboardFolders, setStoryboardFolders] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedSubfolderId, setSelectedSubfolderId] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  
  // Fullscreen preview and trim editor states
  const [previewItem, setPreviewItem] = useState(null); // Item being viewed in fullscreen
  const [trimItem, setTrimItem] = useState(null); // Item being trimmed
  
  const fileInputRef = useRef(null);
  const moveDropdownRef = useRef(null);

  // Close move dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moveDropdownRef.current && !moveDropdownRef.current.contains(e.target)) {
        setShowMoveDropdown(false);
      }
    };
    
    if (showMoveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoveDropdown]);

  // Fetch vault items and folders (filtered by project if projectId provided)
  useEffect(() => {
    const fetchVaultData = async () => {
      if (!userId) return;
      
      try {
        // Build URL with optional project_id filter
        let itemsUrl = `${API_URL}/api/moodboard/vault/items?user_id=${encodeURIComponent(userId)}`;
        let foldersUrl = `${API_URL}/api/moodboard/vault/folders?user_id=${encodeURIComponent(userId)}`;
        
        if (projectId) {
          itemsUrl += `&project_id=${encodeURIComponent(projectId)}`;
          foldersUrl += `&project_id=${encodeURIComponent(projectId)}`;
        }
        
        const [itemsRes, foldersRes] = await Promise.all([
          fetch(itemsUrl),
          fetch(foldersUrl)
        ]);
        
        if (itemsRes.ok) {
          const items = await itemsRes.json();
          setVaultItems(items);
        }
        
        if (foldersRes.ok) {
          const folders = await foldersRes.json();
          setVaultFolders(folders);
        }
      } catch (error) {
        console.error('Failed to fetch vault data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchVaultData();
  }, [userId, projectId]);

  // Handle file upload
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    const newItems = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      
      try {
        // Upload file
        const uploadRes = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          
          // Create vault item with optional project_id
          const itemPayload = {
            user_id: userId,
            name: file.name,
            media_url: uploadData.url,
            media_type: uploadData.is_video ? 'video' : 'image',
            folder_id: selectedFolder,
            tags: []
          };
          
          // Include project_id if we're in a project context
          if (projectId) {
            itemPayload.project_id = projectId;
          }
          
          const itemRes = await fetch(`${API_URL}/api/moodboard/vault/items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(itemPayload)
          });
          
          if (itemRes.ok) {
            const newItem = await itemRes.json();
            newItems.push(newItem);
          }
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }
    
    setVaultItems(prev => [...newItems, ...prev]);
    setUploading(false);
    toast.success(`Uploaded ${newItems.length} of ${files.length} files`);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Create folder
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const folderPayload = {
        name: newFolderName,
        user_id: userId
      };
      
      // Include project_id if we're in a project context
      if (projectId) {
        folderPayload.project_id = projectId;
      }
      
      const res = await fetch(`${API_URL}/api/moodboard/vault/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(folderPayload)
      });
      
      if (res.ok) {
        const folder = await res.json();
        setVaultFolders(prev => [...prev, folder]);
        setNewFolderName('');
        setShowNewFolderModal(false);
        toast.success('Folder created');
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
      toast.error('Failed to create folder');
    }
  };

  // Delete item
  const deleteItem = async (itemId) => {
    try {
      await fetch(`${API_URL}/api/moodboard/vault/items/${itemId}`, {
        method: 'DELETE'
      });
      setVaultItems(prev => prev.filter(i => i.id !== itemId));
      setSelectedItems(prev => prev.filter(id => id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  // Delete folder
  const deleteFolder = async (folderId) => {
    try {
      await fetch(`${API_URL}/api/moodboard/vault/folders/${folderId}`, {
        method: 'DELETE'
      });
      setVaultFolders(prev => prev.filter(f => f.id !== folderId));
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
      toast.success('Folder deleted');
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Move selected items to a folder
  const moveItemsToFolder = async (targetFolderId) => {
    if (selectedItems.length === 0) return;
    
    try {
      // Move each selected item to the target folder
      await Promise.all(
        selectedItems.map(itemId =>
          fetch(`${API_URL}/api/moodboard/vault/items/${itemId}/move`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ folder_id: targetFolderId })
          })
        )
      );
      
      // Update local state
      setVaultItems(prev => 
        prev.map(item => 
          selectedItems.includes(item.id) 
            ? { ...item, folder_id: targetFolderId }
            : item
        )
      );
      
      setSelectedItems([]);
      const folderName = targetFolderId 
        ? vaultFolders.find(f => f.id === targetFolderId)?.name || 'folder'
        : 'All Media';
      if (targetFolderId) {
        toast.success(`Added ${selectedItems.length} item(s) to ${folderName} — still visible in All Media`);
      } else {
        toast.success(`Removed ${selectedItems.length} item(s) from folder`);
      }
    } catch (error) {
      console.error('Failed to move items:', error);
      toast.error('Failed to move items');
    }
  };

  // Add selected items to storyboard
  const addToStoryboard = () => {
    const items = vaultItems.filter(item => selectedItems.includes(item.id));
    onSelectForStoryboard(items);
    setSelectedItems([]);
    toast.success(`Added ${items.length} items to storyboard`);
  };

  // Open Send to Storyboard modal - fetch projects and folders
  const openSendToStoryboardModal = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select items first');
      return;
    }
    if (!userId) {
      toast.error('User not ready. Please try again.');
      return;
    }
    
    try {
      // Fetch storyboard projects (only type=storyboard)
      const projectsRes = await fetch(`${API_URL}/api/moodboard/projects?user_id=${encodeURIComponent(userId)}&type=storyboard`);
      const projectsData = projectsRes.ok ? await projectsRes.json() : [];
      const projectsArr = Array.isArray(projectsData) ? projectsData : [];
      setStoryboardProjects(projectsArr);
      
      // Fetch moodboard folders (which include project subfolders)
      const foldersRes = await fetch(`${API_URL}/api/moodboard/folders?user_id=${encodeURIComponent(userId)}`);
      const foldersData = foldersRes.ok ? await foldersRes.json() : [];
      const foldersArr = Array.isArray(foldersData) ? foldersData : [];
      setStoryboardFolders(foldersArr);
      
      // Reset selection state each time modal opens
      setSelectedProjectId(null);
      setSelectedSubfolderId(null);
      setExpandedProjects(new Set());
      
      setShowSendToStoryboardModal(true);
    } catch (error) {
      console.error('Failed to load storyboard data:', error);
      toast.error('Failed to load storyboard projects');
    }
  };

  // Send selected items to a specific project/folder
  const sendToProjectFolder = async () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }
    
    const items = vaultItems.filter(item => selectedItems.includes(item.id));
    
    // Pass items with folder info to parent
    onSelectForStoryboard(items, selectedProjectId, selectedSubfolderId);
    
    setSelectedItems([]);
    setShowSendToStoryboardModal(false);
    setSelectedProjectId(null);
    setSelectedSubfolderId(null);
    
    const folderName = selectedSubfolderId 
      ? storyboardFolders.find(f => f.id === selectedSubfolderId)?.name 
      : null;
    const projectName = storyboardProjects.find(p => p.id === selectedProjectId)?.name;
    
    toast.success(`Added ${items.length} items to ${projectName}${folderName ? ` / ${folderName}` : ''}`);
  };

  // Get subfolders for a project
  const getProjectSubfolders = (projectId) => {
    return storyboardFolders.filter(f => f.project_id === projectId && !f.parent_id);
  };

  // Get nested subfolders
  const getNestedSubfolders = (parentId) => {
    return storyboardFolders.filter(f => f.parent_id === parentId);
  };

  // Toggle project expansion
  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Duplicate a vault item
  const duplicateItem = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/moodboard/vault/items/${itemId}/duplicate`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to duplicate item');
      }
      
      const duplicatedItem = await response.json();
      setVaultItems(prev => [...prev, duplicatedItem]);
      toast.success('Item duplicated');
    } catch (error) {
      console.error('Failed to duplicate item:', error);
      toast.error('Failed to duplicate item');
    }
  };

  // Filter items by folder and search
  const filteredItems = vaultItems.filter(item => {
    // When "All Media" is selected (selectedFolder is null), show ALL items
    // When a specific folder is selected, show only items in that folder
    const matchesFolder = selectedFolder === null 
      ? true  // Show all items when "All Media" is selected
      : item.folder_id === selectedFolder;
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFolder && matchesSearch;
  });

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 px-3 sm:px-4 py-2 sm:py-3 border-b border-white/20 bg-black/50">
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50"
          >
            <Upload size={14} className="sm:w-4 sm:h-4" />
            {uploading ? `${uploadProgress.current}/${uploadProgress.total}...` : 'Upload'}
          </button>
          <button
            onClick={() => setShowNewFolderModal(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors text-xs sm:text-sm"
          >
            <FolderPlus size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">New Folder</span>
            <span className="sm:hidden">Folder</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleUpload}
            className="hidden"
          />
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:flex-initial">
            <Search size={14} className="sm:w-4 sm:h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-auto pl-8 sm:pl-9 pr-3 sm:pr-4 py-1.5 sm:py-2 bg-white/5 border border-white/20 rounded-lg text-white text-xs sm:text-sm sm:w-48 focus:outline-none focus:border-white/40"
            />
          </div>
          
          {/* View toggle */}
          <div className="flex items-center bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
            >
              <List size={16} />
            </button>
          </div>
          
          {/* Select All / Deselect All toggle — disabled when no items visible */}
          {filteredItems.length > 0 && (() => {
            const allSelected = filteredItems.every(it => selectedItems.includes(it.id));
            return (
              <button
                onClick={() => {
                  if (allSelected) {
                    // Deselect only the currently visible filtered items
                    const visibleIds = new Set(filteredItems.map(it => it.id));
                    setSelectedItems(prev => prev.filter(id => !visibleIds.has(id)));
                  } else {
                    // Select all visible filtered items (merge with any existing selections)
                    const merged = Array.from(new Set([
                      ...selectedItems,
                      ...filteredItems.map(it => it.id),
                    ]));
                    setSelectedItems(merged);
                  }
                }}
                className="px-3 py-2 border border-white/30 text-white/90 rounded-lg hover:bg-white/10 transition-colors text-sm font-medium"
                data-testid="select-all-btn"
              >
                {allSelected ? 'Deselect All' : `Select All${filteredItems.length > 0 ? ` (${filteredItems.length})` : ''}`}
              </button>
            );
          })()}
          
          {/* Selected count and actions */}
          {selectedItems.length > 0 && (
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Move to Folder dropdown */}
              <div className="relative" ref={moveDropdownRef}>
                <button
                  onClick={() => setShowMoveDropdown(!showMoveDropdown)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors text-xs sm:text-sm"
                >
                  <FolderPlus size={14} className="sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Add to Folder</span>
                  <ChevronDown size={12} className="sm:w-3.5 sm:h-3.5" />
                </button>
                
                {showMoveDropdown && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-gray-900 border border-white/20 rounded-lg shadow-xl z-50 py-1">
                    {/* Remove from folder → back to root-only (visible in All Media) */}
                    <button
                      onClick={() => {
                        moveItemsToFolder(null);
                        setShowMoveDropdown(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-white/80 hover:bg-white/10 text-sm"
                    >
                      <Film size={14} />
                      Remove from Folder
                    </button>
                    
                    {vaultFolders.length > 0 && (
                      <div className="border-t border-white/10 my-1" />
                    )}
                    
                    {/* Folder options */}
                    {vaultFolders.map(folder => (
                      <button
                        key={folder.id}
                        onClick={() => {
                          moveItemsToFolder(folder.id);
                          setShowMoveDropdown(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-white/80 hover:bg-white/10 text-sm"
                      >
                        <Folder size={14} style={{ color: folder.color || '#3b82f6' }} />
                        {folder.name}
                      </button>
                    ))}
                    
                    {vaultFolders.length === 0 && (
                      <p className="px-3 py-2 text-white/40 text-xs">No folders yet</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Add to Storyboard button */}
              <button
                onClick={openSendToStoryboardModal}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs sm:text-sm font-medium"
              >
                <Plus size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Send {selectedItems.length} to Storyboard</span>
                <span className="sm:hidden">{selectedItems.length}</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex-1 flex overflow-hidden">
        {/* Folders Sidebar */}
        <div className="w-56 border-r border-white/20 flex flex-col bg-black/30">
          <div className="p-3 border-b border-white/10">
            <h3 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Folders</h3>
          </div>
          <div className="flex-1 overflow-auto p-2">
            {/* All Items */}
            <button
              onClick={() => setSelectedFolder(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors mb-1 ${
                selectedFolder === null 
                  ? 'bg-white/10 text-white' 
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Film size={16} />
              <span className="text-sm">All Media</span>
              <span className="ml-auto text-xs text-white/40">
                {vaultItems.length}
              </span>
            </button>
            
            {/* Folders */}
            {vaultFolders.map(folder => (
              <div key={folder.id} className="group relative">
                <button
                  onClick={() => setSelectedFolder(folder.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors mb-1 ${
                    selectedFolder === folder.id 
                      ? 'bg-white/10 text-white' 
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Folder size={16} style={{ color: folder.color }} />
                  <span className="text-sm truncate">{folder.name}</span>
                  <span className="ml-auto text-xs text-white/40">
                    {vaultItems.filter(i => i.folder_id === folder.id).length}
                  </span>
                </button>
                <button
                  onClick={() => deleteFolder(folder.id)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded text-red-400"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/50" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center">
                  <Video size={32} className="text-white/30" />
                </div>
                <p className="text-white/50 text-lg mb-2">
                  {searchQuery ? 'No items found' : 'Vault is empty'}
                </p>
                <p className="text-white/30 text-sm">
                  {searchQuery ? 'Try a different search' : 'Upload videos and images to your vault'}
                </p>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
              {filteredItems.map(item => (
                <VaultItemCard
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.includes(item.id)}
                  onToggleSelect={() => toggleItemSelection(item.id)}
                  onDelete={() => deleteItem(item.id)}
                  onPreview={() => setPreviewItem(item)}
                  onTrim={() => setTrimItem(item)}
                  onDuplicate={() => duplicateItem(item.id)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredItems.map(item => (
                <VaultItemRow
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.includes(item.id)}
                  onToggleSelect={() => toggleItemSelection(item.id)}
                  onDelete={() => deleteItem(item.id)}
                  onPreview={() => setPreviewItem(item)}
                  onTrim={() => setTrimItem(item)}
                  onDuplicate={() => duplicateItem(item.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowNewFolderModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">New Folder</h3>
              <button onClick={() => setShowNewFolderModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name..."
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-white/40 focus:outline-none"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && createFolder()}
              />
              
              <button
                onClick={createFolder}
                className="w-full py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Fullscreen Video Preview Modal */}
      {previewItem && (
        <FullscreenPreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onTrim={() => {
            setPreviewItem(null);
            setTrimItem(previewItem);
          }}
          onPrev={() => {
            const idx = filteredItems.findIndex(it => it.id === previewItem.id);
            if (idx > 0) setPreviewItem(filteredItems[idx - 1]);
          }}
          onNext={() => {
            const idx = filteredItems.findIndex(it => it.id === previewItem.id);
            if (idx >= 0 && idx < filteredItems.length - 1) setPreviewItem(filteredItems[idx + 1]);
          }}
          hasPrev={filteredItems.findIndex(it => it.id === previewItem.id) > 0}
          hasNext={(() => {
            const idx = filteredItems.findIndex(it => it.id === previewItem.id);
            return idx >= 0 && idx < filteredItems.length - 1;
          })()}
        />
      )}
      
      {/* Video Trim Editor Modal */}
      {trimItem && trimItem.media_type === 'video' && (
        <VideoTrimModal
          item={trimItem}
          userId={userId}
          onClose={() => setTrimItem(null)}
          onSave={(trimData) => {
            // Update the item in state with the new trimmed video URL
            setVaultItems(prev => prev.map(item => 
              item.id === trimItem.id 
                ? { 
                    ...item, 
                    trim_start: trimData.trim_start, 
                    trim_end: trimData.trim_end,
                    media_url: trimData.media_url || item.media_url,
                    trimmed: trimData.trimmed || item.trimmed
                  }
                : item
            ));
            setTrimItem(null);
          }}
        />
      )}

      {/* Send to Storyboard Modal */}
      {showSendToStoryboardModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowSendToStoryboardModal(false)}
          data-testid="send-to-storyboard-modal"
        >
          <div 
            className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-white text-lg font-semibold">Send to Storyboard</h3>
              <button 
                onClick={() => setShowSendToStoryboardModal(false)}
                className="text-white/60 hover:text-white"
                data-testid="close-send-to-storyboard-modal"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Selected items preview */}
            <div className="px-6 py-3 border-b border-white/10 bg-white/5">
              <p className="text-white/60 text-sm">
                Sending <span className="text-white font-medium">{selectedItems.length} item(s)</span> to storyboard
              </p>
            </div>
            
            {/* Project & Folder Selection */}
            <div className="px-6 py-4 overflow-y-auto max-h-[50vh]">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Select Project & Folder</p>
              
              {storyboardProjects.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-8">No storyboard projects found. Create one on the Storyboard tab first.</p>
              ) : (
                <div className="space-y-1">
                  {storyboardProjects.map(project => {
                    const projectFolders = getProjectSubfolders(project.id);
                    const hasSubfolders = projectFolders.length > 0;
                    const isExpanded = expandedProjects.has(project.id);
                    const isProjectSelected = selectedProjectId === project.id && !selectedSubfolderId;
                    
                    // Recursive folder renderer
                    const renderFolder = (folder, depth = 1) => {
                      const nestedFolders = getNestedSubfolders(folder.id);
                      const hasNested = nestedFolders.length > 0;
                      const isFolderSelected = selectedSubfolderId === folder.id;
                      
                      return (
                        <div key={folder.id}>
                          <div
                            onClick={() => {
                              setSelectedProjectId(project.id);
                              setSelectedSubfolderId(folder.id);
                            }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                              isFolderSelected 
                                ? 'bg-blue-500/20 text-white border border-blue-500/50' 
                                : 'text-white/70 hover:bg-white/5'
                            }`}
                            style={{ marginLeft: `${depth * 20}px` }}
                            data-testid={`storyboard-folder-${folder.id}`}
                          >
                            <Folder size={14} className="text-blue-400 flex-shrink-0" />
                            <span className="text-sm">{folder.name}</span>
                            {isFolderSelected && <Check size={14} className="ml-auto text-blue-400" />}
                          </div>
                          {hasNested && nestedFolders.map(nf => renderFolder(nf, depth + 1))}
                        </div>
                      );
                    };
                    
                    return (
                      <div key={project.id} className="mb-2">
                        {/* Project row */}
                        <div
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                            isProjectSelected 
                              ? 'bg-blue-500/20 text-white border border-blue-500/50' 
                              : 'text-white/80 hover:bg-white/5'
                          }`}
                          onClick={() => {
                            setSelectedProjectId(project.id);
                            setSelectedSubfolderId(null);
                            if (hasSubfolders) {
                              toggleProjectExpansion(project.id);
                            }
                          }}
                          data-testid={`storyboard-project-${project.id}`}
                        >
                          {hasSubfolders && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleProjectExpansion(project.id);
                              }}
                              className="p-0.5 cursor-pointer"
                            >
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                          )}
                          {!hasSubfolders && <div className="w-5" />}
                          <Film size={16} className="text-purple-400 flex-shrink-0" />
                          <span className="text-sm font-medium">{project.name}</span>
                          {isProjectSelected && <Check size={14} className="ml-auto text-blue-400" />}
                        </div>
                        
                        {/* Subfolders */}
                        {isExpanded && hasSubfolders && (
                          <div className="mt-1">
                            {projectFolders.map(folder => renderFolder(folder, 1))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setShowSendToStoryboardModal(false)}
                className="px-4 py-2 text-white/70 hover:text-white transition-colors"
                data-testid="cancel-send-to-storyboard"
              >
                Cancel
              </button>
              <button
                onClick={sendToProjectFolder}
                disabled={!selectedProjectId}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-send-to-storyboard"
              >
                Send to {selectedSubfolderId ? 'Folder' : 'Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Vault Item Card Component - Optimized with thumbnails and lazy loading
const VaultItemCard = ({ item, isSelected, onToggleSelect, onDelete, onPreview, onTrim, onDuplicate }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [thumbnail, setThumbnail] = useState(item.thumbnail_data_uri || null);
  const [isVisible, setIsVisible] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const cardRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fetch thumbnail for videos that don't have one
  useEffect(() => {
    if (isVisible && item.media_type === 'video' && !thumbnail && item.id) {
      fetch(`${API_URL}/api/moodboard/vault/thumbnail/${item.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.thumbnail) {
            setThumbnail(data.thumbnail);
          }
        })
        .catch(() => {});
    }
  }, [isVisible, item.id, item.media_type, thumbnail]);

  // Handle video hover play - only load video on first hover
  useEffect(() => {
    if (videoRef.current && item.media_type === 'video' && videoLoaded) {
      if (isHovering) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovering, item.media_type, videoLoaded]);

  // Load video only when user hovers
  const handleMouseEnter = () => {
    setIsHovering(true);
    if (item.media_type === 'video' && !videoLoaded) {
      setVideoLoaded(true);
    }
  };

  // Immediately pause & rewind on mouse leave so the thumbnail stops moving instantly
  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (_) { /* no-op */ }
    }
  };

  return (
    <div 
      ref={cardRef}
      className={`relative group rounded-lg overflow-hidden border transition-all cursor-pointer ${
        isSelected 
          ? 'border-blue-500 ring-2 ring-blue-500/50' 
          : 'border-white/20 hover:border-white/40'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onToggleSelect}
    >
      {/* Media */}
      <div className="aspect-video bg-black relative">
        {item.media_type === 'video' ? (
          <>
            {/* Show thumbnail by default, load video on hover */}
            {thumbnail && !isHovering ? (
              <img 
                src={thumbnail} 
                alt={item.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : videoLoaded ? (
              <video
                ref={videoRef}
                src={item.media_url}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="auto"
                poster={thumbnail || undefined}
                onLoadedData={() => {
                  if (isHovering && videoRef.current) {
                    videoRef.current.play().catch(() => {});
                  }
                }}
              />
            ) : (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <Film size={32} className="text-gray-600" />
              </div>
            )}
            {!isHovering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Play size={24} className="text-white" fill="white" />
              </div>
            )}
          </>
        ) : (
          isVisible ? (
            <img 
              src={item.media_url} 
              alt={item.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
              <ImageIcon size={32} className="text-gray-600" />
            </div>
          )
        )}
        
        {/* Selection checkbox */}
        <div className={`absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
          isSelected 
            ? 'bg-blue-500 border-blue-500' 
            : 'border-white/50 bg-black/30 opacity-0 group-hover:opacity-100'
        }`}>
          {isSelected && <Check size={14} className="text-white" />}
        </div>
        
        {/* Action buttons - View & Trim */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            className="p-1.5 bg-black/70 hover:bg-black/90 rounded text-white"
            title="View fullscreen"
          >
            <Maximize2 size={14} />
          </button>
          {item.media_type === 'video' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTrim();
              }}
              className="p-1.5 bg-black/70 hover:bg-black/90 rounded text-white"
              title="Trim video"
            >
              <Scissors size={14} />
            </button>
          )}
        </div>
        
        {/* Menu button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="absolute top-2 right-2 p-1 bg-black/60 rounded opacity-0 group-hover:opacity-100"
        >
          <MoreVertical size={14} className="text-white" />
        </button>
        
        {showMenu && (
          <div 
            className="absolute top-8 right-2 bg-black border border-white/30 rounded-lg py-1 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPreview();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2 text-left text-white hover:bg-white/10 text-sm flex items-center gap-2"
            >
              <Maximize2 size={14} />
              View Fullscreen
            </button>
            {item.media_type === 'video' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTrim();
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-white hover:bg-white/10 text-sm flex items-center gap-2"
              >
                <Scissors size={14} />
                Trim Video
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2 text-left text-white hover:bg-white/10 text-sm flex items-center gap-2"
            >
              <Copy size={14} />
              Duplicate
            </button>
            <button
              onClick={() => {
                onDelete();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2 text-left text-red-400 hover:bg-white/10 text-sm flex items-center gap-2"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>
      
      {/* Name */}
      <div className="p-2 bg-black/50">
        <p className="text-white text-sm truncate">{item.name}</p>
      </div>
    </div>
  );
};


// Vault Item Row Component (for list view) - Optimized with thumbnails
const VaultItemRow = ({ item, isSelected, onToggleSelect, onDelete, onPreview, onTrim, onDuplicate }) => {
  const [thumbnail, setThumbnail] = useState(item.thumbnail_data_uri || null);
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px' }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fetch thumbnail for videos
  useEffect(() => {
    if (isVisible && item.media_type === 'video' && !thumbnail && item.id) {
      fetch(`${API_URL}/api/moodboard/vault/thumbnail/${item.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.thumbnail) {
            setThumbnail(data.thumbnail);
          }
        })
        .catch(() => {});
    }
  }, [isVisible, item.id, item.media_type, thumbnail]);

  return (
    <div 
      ref={rowRef}
      className={`group flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-all ${
        isSelected 
          ? 'border-blue-500 bg-blue-500/10' 
          : 'border-white/10 hover:border-white/20 hover:bg-white/5'
      }`}
      onClick={onToggleSelect}
    >
      {/* Checkbox */}
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
        isSelected 
          ? 'bg-blue-500 border-blue-500' 
          : 'border-white/30'
      }`}>
        {isSelected && <Check size={14} className="text-white" />}
      </div>
      
      {/* Thumbnail - use generated thumbnail instead of loading full video */}
      <div className="w-16 h-10 rounded overflow-hidden flex-shrink-0 bg-gray-800">
        {item.media_type === 'video' ? (
          thumbnail ? (
            <img src={thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film size={16} className="text-gray-600" />
            </div>
          )
        ) : (
          isVisible ? (
            <img src={item.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon size={16} className="text-gray-600" />
            </div>
          )
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate">{item.name}</p>
        <p className="text-white/40 text-xs">{item.media_type === 'video' ? 'Video' : 'Image'}</p>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className="p-2 text-white hover:bg-white/10 rounded"
          title="View fullscreen"
        >
          <Maximize2 size={16} />
        </button>
        {item.media_type === 'video' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTrim();
            }}
            className="p-2 text-white hover:bg-white/10 rounded"
            title="Trim video"
          >
            <Scissors size={16} />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          className="p-2 text-white hover:bg-white/10 rounded"
          title="Duplicate"
        >
          <Copy size={16} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-2 text-red-400 hover:bg-white/10 rounded"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};


// Fullscreen Preview Modal Component
const FullscreenPreviewModal = ({ item, onClose, onTrim, onPrev, onNext, hasPrev, hasNext }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle keyboard shortcuts: Esc = close, Space = play/pause, ArrowLeft/Right = prev/next
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === ' ' && item.media_type === 'video') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isPlaying, item.media_type, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div 
      className="fixed inset-0 bg-black/95 flex flex-col z-50"
      onClick={onClose}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-6 py-4 border-b border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          {item.media_type === 'video' ? (
            <Video size={20} className="text-blue-400" />
          ) : (
            <ImageIcon size={20} className="text-green-400" />
          )}
          <h3 className="text-white font-medium">{item.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          {item.media_type === 'video' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTrim();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm"
            >
              <Scissors size={16} />
              Trim Video
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
          >
            <X size={24} />
          </button>
        </div>
      </div>
      
      {/* Media Container with Prev/Next arrows */}
      <div 
        className="flex-1 flex items-center justify-center p-8 overflow-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous arrow */}
        {onPrev && (
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            disabled={!hasPrev}
            aria-label="Previous"
            data-testid="preview-prev-btn"
            className="absolute left-64 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/60 border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-20 disabled:cursor-not-allowed z-10"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {/* Next arrow */}
        {onNext && (
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            disabled={!hasNext}
            aria-label="Next"
            data-testid="preview-next-btn"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/60 border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-20 disabled:cursor-not-allowed z-10"
          >
            <ChevronRight size={28} />
          </button>
        )}

        {item.media_type === 'video' ? (
          <div className="relative max-w-full max-h-full">
            <video
              ref={videoRef}
              key={item.id}
              src={item.media_url}
              className="max-w-full max-h-[calc(100vh-160px)] rounded-lg"
              controls
              autoPlay
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          </div>
        ) : (
          <img
            key={item.id}
            src={item.media_url}
            alt={item.name}
            className="max-w-full max-h-[calc(100vh-160px)] object-contain rounded-lg"
          />
        )}
      </div>
      
      {/* Footer hint */}
      <div className="px-6 py-3 text-center text-white/40 text-sm border-t border-white/10">
        Press ESC to close {item.media_type === 'video' ? '• Space to play/pause' : ''} • ← / → to navigate
      </div>
    </div>
  );
};


// Video Trim Modal Component
const VideoTrimModal = ({ item, userId, onClose, onSave }) => {
  const [isTrimming, setIsTrimming] = useState(false);
  const [pendingTrimData, setPendingTrimData] = useState(null);
  
  // Called when user sets trim points in the editor (doesn't save yet)
  const handleTrimPointsChange = (trimData) => {
    console.log('Trim points updated:', trimData);
    setPendingTrimData(trimData);
    // Don't call API here - just store the pending trim data
  };
  
  // Called when user clicks "Save Changes" to actually trim and save
  const handleSaveChanges = async () => {
    if (!pendingTrimData) {
      toast.error('Please set trim points first using Set Start and Set End buttons');
      return;
    }
    
    const trimData = pendingTrimData;
    console.log('Saving trim with data:', trimData);
    console.log('Video URL:', item.media_url);
    
    // Validate trim data
    if (trimData.trim_start === undefined || trimData.trim_start === null) {
      toast.error('Invalid start time');
      return;
    }
    
    if (trimData.trim_end === undefined || trimData.trim_end === null) {
      toast.error('Please set an end time for the trim');
      return;
    }
    
    if (trimData.trim_end <= trimData.trim_start) {
      toast.error('End time must be greater than start time');
      return;
    }
    
    setIsTrimming(true);
    toast.loading('Trimming video... This may take a moment.', { id: 'trim-video' });
    
    const requestBody = {
      video_url: item.media_url,
      trim_start: trimData.trim_start,
      trim_end: trimData.trim_end,
      user_id: userId,
      item_id: item.id
    };
    
    console.log('Sending trim request:', requestBody);
    
    // Use XMLHttpRequest to avoid fetch Response body issues
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/moodboard/vault/trim-video`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      setIsTrimming(false);
      toast.dismiss('trim-video');
      
      console.log('XHR response status:', xhr.status);
      console.log('XHR response text:', xhr.responseText);
      
      try {
        const responseData = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success('Video trimmed and saved successfully!');
          
          // Update the parent with the new trimmed video URL
          onSave({
            ...trimData,
            media_url: responseData.trimmed_url,
            trimmed: true
          });
          
          onClose();
        } else {
          const errorMsg = responseData.detail || responseData.message || 'Failed to trim video';
          console.error('Trim API error:', errorMsg);
          toast.error(errorMsg);
        }
      } catch (parseError) {
        console.error('Failed to parse response:', xhr.responseText);
        toast.error('Server returned an invalid response');
      }
    };
    
    xhr.onerror = function() {
      setIsTrimming(false);
      toast.dismiss('trim-video');
      console.error('XHR network error');
      toast.error('Network error occurred while trimming video');
    };
    
    xhr.ontimeout = function() {
      setIsTrimming(false);
      toast.dismiss('trim-video');
      toast.error('Request timed out. The video may be too long.');
    };
    
    xhr.timeout = 300000; // 5 minutes timeout for video processing
    xhr.send(JSON.stringify(requestBody));
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/95 flex flex-col z-50"
      onClick={onClose}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-6 py-4 border-b border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <Scissors size={20} className="text-blue-400" />
          <h3 className="text-white font-medium">Trim Video: {item.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          {isTrimming && (
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-400 border-t-transparent" />
              Processing...
            </div>
          )}
          <button
            onClick={onClose}
            disabled={isTrimming}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>
      </div>
      
      {/* Trim Editor Container */}
      <div 
        className="flex-1 flex items-center justify-center p-8 overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-4xl">
          <VideoTrimEditor
            videoUrl={item.media_url}
            trimStart={item.trim_start || 0}
            trimEnd={item.trim_end}
            onTrimChange={handleTrimPointsChange}
            onCaptureThumbnail={(url) => {
              toast.success('Thumbnail captured!');
            }}
            userId={userId}
          />
          
          {/* Info banner and Save button */}
          <div className="mt-4 space-y-4">
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-300 text-sm text-center">
                1. Use Set Start / Set End buttons to mark your trim points<br/>
                2. Click Preview to check your selection<br/>
                3. Click "Save Changes" below to create the trimmed video
              </p>
            </div>
            
            {/* Save Changes Button */}
            <div className="flex items-center justify-between">
              <div className="text-white/50 text-sm">
                {pendingTrimData ? (
                  <span className="text-green-400">
                    Trim: {pendingTrimData.trim_start?.toFixed(2)}s - {pendingTrimData.trim_end?.toFixed(2)}s
                  </span>
                ) : (
                  <span>No trim points set</span>
                )}
              </div>
              <button
                onClick={handleSaveChanges}
                disabled={isTrimming || !pendingTrimData}
                className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTrimming ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Trimming...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Send to Storyboard Modal is rendered by the parent VideoVault component */}
    </div>
  );
};

export default VideoVault;
